/*! @license
MIT License

Copyright (c) 2026 T3 Tools Inc.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
// Adapted from T3 Code 62ed748ac94a73dca1b3eb99b1da52d71ebed247.
// Copyright (c) 2026 T3 Tools Inc. MIT; see docs/licenses/T3-Code-MIT.txt.
import * as Schema from "effect/Schema";

/** Includes allocation plus the host verification window. */
export const connectLinkRequestTimeoutMs = 120000;
/**
 * How long a Connections read may stay unanswered before the surface waiting on it stops
 * presenting itself as merely busy and offers a retry instead. It bounds what the user is shown,
 * not the call, and has to stay far below `connectLinkRequestTimeoutMs` — which one Connect
 * operation may legitimately spend — or a waiting surface looks broken for minutes.
 */
export const connectPendingReadTimeoutMs = 20000;

export type ConnectFetch = (request: Request) => Promise<Response>;
export class ConnectError extends Error {
  readonly code: string;
  readonly status: number;
  /**
   * Diagnostics only, never behaviour. `code` is the contract a client may act
   * on, and stays collapsed to the few refusals it is allowed to distinguish; a
   * client that could branch on every server code would couple itself to the
   * server's internals. Whoever reads the log still needs to know which route
   * refused and what it said, so both ride along here, untrusted and unacted-on.
   */
  readonly route: string | undefined;
  readonly serverCode: string | undefined;
  constructor(
    code: string,
    status = 400,
    options: ErrorOptions & { readonly route?: string; readonly serverCode?: string } = {},
  ) {
    super(code, options);
    this.code = code;
    this.status = status;
    this.route = options.route;
    this.serverCode = options.serverCode;
  }
}

/**
 * A Connect origin that cannot be reached at all — DNS, TLS, a refused socket or
 * the request timeout — fails the transport rather than the HTTP exchange, so it
 * surfaces as a DOMException or an undici TypeError with no Connect vocabulary.
 * Callers recognise Connect failures by ConnectError alone, and the Cloud
 * Workspace warm-up loops retry 503 while a freshly started workspace becomes routable,
 * so an unreachable origin has to enter that vocabulary or it escapes both.
 * A caller cancelling its own request is not a failure of the origin, so an
 * AbortError passes through and ends the call.
 */
function connectTransportFailure(error: unknown) {
  if (error instanceof ConnectError) return error;
  if (error instanceof Error && error.name === "AbortError") return error;
  return new ConnectError("connect_unreachable", 503, { cause: error });
}

/**
 * What actually failed, for the log the host keeps — never for a user.
 *
 * Every Connect operation reports one sentence to the person waiting, which is
 * right: a relay that is down, a rejected token and an expired code are the
 * same thing to someone who just wants to connect. They are not the same thing
 * to whoever has to fix it, and discarding the cause left no record anywhere of
 * which one it was. Only fixed strings are repeated — relay codes, statuses and
 * routes — because provider bodies and credentials never reach a `ConnectError`.
 *
 * An unreachable origin is the reason the wrapped cause is named too: its own
 * code is always `connect_unreachable`, so without it the log would say a
 * refused socket and an expired TLS certificate in exactly the same words.
 */
export const describeConnectFailure = (cause: unknown): string => {
  if (cause instanceof ConnectError) {
    const detail = [
      `status ${cause.status}`,
      ...(cause.serverCode === undefined ? [] : [`server said ${cause.serverCode}`]),
      ...(cause.route === undefined ? [] : [cause.route]),
      ...(cause.cause === undefined ? [] : [describeFailure(cause.cause)]),
    ].join(", ");
    return `ConnectError ${cause.code} (${detail})`;
  }
  return describeFailure(cause);
};

/** One line naming a thrown value, whatever it turned out to be. */
export const describeFailure = (cause: unknown): string =>
  cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause);

/** A short snake_case token from a `{"error": code}` body; anything else is not repeated. */
export const serverErrorCode = (body: unknown): string | undefined =>
  typeof body === "object" &&
  body !== null &&
  "error" in body &&
  typeof body.error === "string" &&
  /^[a-z][a-z0-9_]{0,63}$/.test(body.error)
    ? body.error
    : undefined;

export function requireConnect(condition: unknown, code: string, status = 400): asserts condition {
  if (!condition) throw new ConnectError(code, status);
}
export const randomConnectToken = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return encodeBytes(bytes);
};
const encodeBytes = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const decodeBytes = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
const encode = (value: unknown) => new TextEncoder().encode(JSON.stringify(value));
export async function connectKeyPair() {
  const pair = (await crypto.subtle.generateKey({ name: "Ed25519" }, true, [
    "sign",
    "verify",
  ])) as unknown as {
    privateKey: Parameters<typeof crypto.subtle.exportKey>[1];
    publicKey: Parameters<typeof crypto.subtle.exportKey>[1];
  };
  return {
    privateKey: encodeBytes(
      new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey)),
    ),
    publicKey: encodeBytes(new Uint8Array(await crypto.subtle.exportKey("spki", pair.publicKey))),
  };
}
export async function signConnect(value: unknown, privateKey: string) {
  const key = await crypto.subtle.importKey("pkcs8", decodeBytes(privateKey), "Ed25519", false, [
    "sign",
  ]);
  return encodeBytes(new Uint8Array(await crypto.subtle.sign("Ed25519", key, encode(value))));
}
export async function verifyConnect(value: unknown, signature: string, publicKey: string) {
  try {
    const key = await crypto.subtle.importKey("spki", decodeBytes(publicKey), "Ed25519", false, [
      "verify",
    ]);
    return await crypto.subtle.verify("Ed25519", key, decodeBytes(signature), encode(value));
  } catch {
    return false;
  }
}
export async function connectDigest(value: string) {
  return encodeBytes(
    new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value))),
  );
}
export function connectOrigin(value: string) {
  const url = new URL(value);
  requireConnect(
    (url.protocol === "https:" || (url.protocol === "http:" && url.hostname === "127.0.0.1")) &&
      url.pathname === "/" &&
      !url.search &&
      !url.hash &&
      !url.username &&
      !url.password,
    "invalid_connect_origin",
  );
  return url.origin;
}
export const connectJson = (value: unknown, status = 200) =>
  Response.json(value, {
    status,
    headers: { "cache-control": "no-store" },
  });
/**
 * The one label an unexpected failure is allowed to carry off the host.
 *
 * `connect_unavailable` is the whole account a client gets of a 503, so without
 * this the cause of an outage exists nowhere: not in the body, not in the log.
 * An error's message is not safe to keep — a driver puts the connection's host,
 * role, and sometimes its password in there — so only the class and a driver's
 * own status code cross this boundary. SQLSTATE is the field worth carrying: it
 * separates a missing table from a refused login without naming either.
 */
export function connectFailureCategory(error: unknown) {
  const bounded = (value: unknown) =>
    typeof value === "string" && /^[A-Za-z0-9_]{1,32}$/.test(value) ? value : undefined;
  if (error instanceof ConnectError) return error.code;
  const name = bounded(error instanceof Error ? error.name : undefined) ?? "unknown";
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? bounded((error as { code: unknown }).code)
      : undefined;
  return code ? `${name}:${code}` : name;
}

/**
 * `reportFailure` receives the category of an unexpected throw, never a
 * ConnectError: a refusal the code already named is not a failure to report.
 */
export async function connectResponse(
  operation: () => Promise<Response>,
  reportFailure?: (category: string) => void,
) {
  try {
    return await operation();
  } catch (error) {
    if (!(error instanceof ConnectError) && reportFailure) {
      try {
        reportFailure(connectFailureCategory(error));
      } catch {
        /* Reporting cannot change application behavior. */
      }
    }
    return connectJson(
      { error: error instanceof ConnectError ? error.code : "connect_unavailable" },
      error instanceof ConnectError ? error.status : 503,
    );
  }
}
export async function readConnect<A>(
  request: Request,
  schema: Schema.Decoder<A, never>,
  maxBytes = 16384,
) {
  requireConnect(
    Number(request.headers.get("content-length") ?? 0) <= maxBytes,
    "request_too_large",
  );
  const reader = request.body?.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  if (reader) {
    try {
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        size += chunk.value.byteLength;
        if (size > maxBytes) {
          void reader.cancel().catch(() => undefined);
          throw new ConnectError("request_too_large", 413);
        }
        chunks.push(chunk.value);
      }
    } finally {
      reader.releaseLock();
    }
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  const text = new TextDecoder().decode(bytes);
  try {
    return Schema.decodeUnknownSync(schema)(JSON.parse(text));
  } catch {
    throw new ConnectError("invalid_request");
  }
}
export async function connectCall<A>(
  fetcher: ConnectFetch,
  origin: string,
  path: string,
  token: string | undefined,
  body: unknown,
  schema: Schema.Decoder<A, never>,
  timeoutMs = 30000,
) {
  const request = new Request(`${connectOrigin(origin)}${path}`, {
    method: body === undefined ? "GET" : "POST",
    redirect: "error",
    signal: AbortSignal.timeout(timeoutMs),
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const response = await fetcher(request).catch((error: unknown) => {
    throw connectTransportFailure(error);
  });
  if (!response.ok) throw await connectHttpFailure(response, path);
  // A body that is not JSON at all is not Connect answering: a captive portal or a proxy page
  // after the computer wakes. It fails the transport, like the origin being unreachable.
  const payload: unknown = await response.json().catch((error: unknown) => {
    throw connectTransportFailure(error);
  });
  return Schema.decodeUnknownSync(schema)(payload);
}

/**
 * The refusals a client is allowed to tell apart, each pinned to the status it is meant to arrive
 * with. Anything else collapses to the transport status, so no caller couples itself to a server
 * internal, and a code carried at the wrong status is treated as one of those internals.
 *
 * A refusal earns a place here by being something the person in front of the app can act on:
 * waiting out a quota, shortening a report, signing in, or being told plainly that retrying will
 * not help. Until #1008 the feedback refusals were absent, so ADR-0105's quota, blocklist, payload
 * caps and circuit breaker all reached the user as "check your connection".
 */
const ACTIONABLE_CONNECT_REFUSALS: Readonly<Record<string, number>> = {
  feedback_quota: 429,
  feedback_blocked: 429,
  feedback_too_large: 413,
  feedback_unavailable: 503,
  identity_unavailable: 503,
  // A Cloud Workspace's run owns its folder, or none is live to connect to: the client offers to
  // start one, or to edit files once the run has stopped.
  workspace_running: 409,
  workspace_stopped: 409,
  workspace_starting: 503,
  // Its run failed to start several times in a row; Start is what tries again (#1100).
  workspace_failed: 409,
  // Starting a run needs Usage Allowance; connecting says so rather than "unreachable".
  insufficient_credits: 402,
  file_too_large: 413,
  file_not_found: 404,
  file_exists: 409,
  // AFAR Networks and Organizations (ADR-0146, ADR-0148): each names what the person can do next.
  networks_unavailable: 503,
  network_denied: 404,
  network_manage_denied: 403,
  machine_not_found: 404,
  machine_offline: 503,
  machine_unavailable: 503,
  machine_cannot_host: 409,
  limits_need_organization: 409,
  organization_denied: 404,
  organization_owner_required: 403,
  organization_has_machines: 409,
  last_owner: 409,
  member_not_found: 409,
  invalid_email: 400,
  invalid_organization_name: 400,
  invitation_invalid: 404,
  invitation_wrong_email: 409,
  invitation_expired: 409,
  invitation_already_used: 409,
  invitation_revoked: 409,
};

/**
 * The refusal a non-OK Connect response stands for, carrying what
 * `describeConnectFailure` needs to name it later: the status, the route, and the
 * Connect's own code when the body carried a repeatable one.
 *
 * `code` names the check a caller was making, for whom that is the more useful
 * name than the transport's. Left out, an actionable refusal keeps the code a
 * client may branch on and every other one collapses to the status, so no caller
 * couples itself to a server internal.
 */
export async function connectHttpFailure(response: Response, route: string, code?: string) {
  const body: unknown = await response.json().catch(() => undefined);
  const serverCode = serverErrorCode(body);
  const actionable =
    serverCode !== undefined && ACTIONABLE_CONNECT_REFUSALS[serverCode] === response.status;
  return new ConnectError(
    code ?? (actionable ? serverCode : `connect_http_${response.status}`),
    response.status,
    { route, ...(serverCode === undefined ? {} : { serverCode }) },
  );
}

/** Exact-origin CORS for public Connect requests. Preflight grants no application authority. */
export async function connectBrowserResponse(
  request: Request,
  allowedOrigins: readonly string[],
  /** Kept ahead of the operation so a caller's inline handler stays the final argument. */
  reportFailure: ((category: string) => void) | undefined,
  operation: () => Promise<Response>,
) {
  const origin = request.headers.get("origin");
  if (origin === null) return connectResponse(operation, reportFailure);
  if (!allowedOrigins.includes(origin)) return connectJson({ error: "wrong_origin" }, 403);
  const headers = {
    "access-control-allow-origin": origin,
    vary: "Origin",
    "cache-control": "no-store",
  };
  if (request.method === "OPTIONS") {
    const method = request.headers.get("access-control-request-method");
    const requestedHeaders = (request.headers.get("access-control-request-headers") ?? "")
      .toLowerCase()
      .split(",")
      .map((header) => header.trim())
      .filter(Boolean);
    if (
      !method ||
      !["GET", "POST"].includes(method) ||
      requestedHeaders.some((header) => !["authorization", "content-type"].includes(header))
    )
      return connectJson({ error: "preflight_denied" }, 403);
    return new Response(null, {
      status: 204,
      headers: {
        ...headers,
        "access-control-allow-methods": "GET, POST",
        "access-control-allow-headers": "authorization, content-type",
        // Browsers cap this (Chrome at two hours); without it they ask before nearly every request.
        "access-control-max-age": "600",
      },
    });
  }
  const response = await connectResponse(operation, reportFailure);
  const result = new Response(response.body, response);
  for (const [key, value] of Object.entries(headers)) result.headers.set(key, value);
  return result;
}
